import { customDomainUrl, normalizeCustomDomainInput } from '../../utils/customDomain';

export const SAAS_ORG_SLUG_KEY = 'saas_pending_org_slug';

export const DEFAULT_SAAS_PUBLIC_ORIGIN = 'https://serviceapp.ai';

function normalizeEmbedOrigin(origin: string): string {
  const trimmed = origin.trim().replace(/\/$/, '');
  try {
    const { hostname } = new URL(trimmed);
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return DEFAULT_SAAS_PUBLIC_ORIGIN;
    }
    return trimmed;
  } catch {
    return DEFAULT_SAAS_PUBLIC_ORIGIN;
  }
}

export function captureOrgSlugFromSearch(): void {
  if (typeof window === 'undefined') return;
  const org = new URLSearchParams(window.location.search).get('org');
  if (org?.trim()) {
    localStorage.setItem(SAAS_ORG_SLUG_KEY, org.trim());
  }
}

/** Drop a member-login slug so a later ServiceApp workspace login is not treated as that organisation. */
export function clearPendingOrgSlug(): void {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(SAAS_ORG_SLUG_KEY);
}

export interface SaasEmbedUrls {
  customerLogin: string;
  customerSignup: string;
  bookOnline: string;
  /** Shareable public availability calendar (no login required to view dates). */
  availability: string;
  publicSite: string;
  aiChat: string;
  staffLogin: string;
  /** Hosted catalog. Closed until the organisation turns the online store on. */
  store: string;
  /** Membership application form. Closed unless the organisation requires applications. */
  apply: string;
  orgSlug: string;
  origin: string;
  usesCustomDomain: boolean;
}

export function resolveSaasPublicEmbedOrigin(options?: {
  explicitOrigin?: string;
  customDomain?: string | null;
}): string {
  if (options?.customDomain?.trim()) {
    const url = customDomainUrl(options.customDomain);
    if (url) return url;
  }

  if (options?.explicitOrigin?.trim()) {
    return normalizeEmbedOrigin(options.explicitOrigin);
  }

  const env =
    (typeof import.meta !== 'undefined' &&
      (import.meta.env?.VITE_SAAS_APP_URL as string | undefined)?.trim()) ||
    (typeof import.meta !== 'undefined' && (import.meta.env?.VITE_APP_URL as string | undefined)?.trim()) ||
    '';

  if (env) return normalizeEmbedOrigin(env);

  if (typeof window !== 'undefined') {
    const { origin, hostname } = window.location;
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return DEFAULT_SAAS_PUBLIC_ORIGIN;
    }
    return normalizeEmbedOrigin(origin);
  }

  return DEFAULT_SAAS_PUBLIC_ORIGIN;
}

function orgScopedPath(base: string, path: string, orgSlug: string, usesCustomDomain: boolean): string {
  if (usesCustomDomain) return `${base}${path}`;
  const join = path.includes('?') ? '&' : '?';
  return `${base}${path}${join}org=${encodeURIComponent(orgSlug)}`;
}

export function buildSaasEmbedUrls(
  orgSlug: string,
  options?: { origin?: string; customDomain?: string | null },
): SaasEmbedUrls {
  const customDomain = options?.customDomain?.trim() ? normalizeCustomDomainInput(options.customDomain) : '';
  const usesCustomDomain = Boolean(customDomain);
  const base = resolveSaasPublicEmbedOrigin({
    explicitOrigin: options?.origin,
    customDomain: customDomain || null,
  });
  const slug = orgSlug.trim();

  return {
    orgSlug: slug,
    origin: base,
    usesCustomDomain,
    customerLogin: orgScopedPath(base, '/login', slug, usesCustomDomain),
    customerSignup: orgScopedPath(base, '/signup', slug, usesCustomDomain),
    bookOnline: orgScopedPath(base, '/book', slug, usesCustomDomain),
    availability: orgScopedPath(base, '/availability', slug, usesCustomDomain),
    publicSite: orgScopedPath(base, '/site', slug, usesCustomDomain),
    aiChat: orgScopedPath(base, '/ai-chat', slug, usesCustomDomain),
    staffLogin: `${base}/login`,
    store: orgScopedPath(base, '/store', slug, usesCustomDomain),
    apply: orgScopedPath(base, '/apply', slug, usesCustomDomain),
  };
}

export function applicationStatusUrl(urls: Pick<SaasEmbedUrls, 'origin'>, token: string): string {
  return `${urls.origin}/apply/status?token=${encodeURIComponent(token)}`;
}

export function bookNowButtonHtml(urls: SaasEmbedUrls, label = 'Book online'): string {
  return `<a href="${urls.bookOnline}" target="_blank" rel="noopener noreferrer" style="display:inline-block;padding:12px 24px;background:linear-gradient(135deg,#6366f1,#06b6d4);color:#fff;font-family:system-ui,sans-serif;font-weight:600;font-size:16px;text-decoration:none;border-radius:9999px;">${label}</a>`;
}

export function customerLoginLinkHtml(urls: SaasEmbedUrls, label = 'Customer login'): string {
  return `<a href="${urls.customerLogin}" target="_blank" rel="noopener noreferrer" style="font-family:system-ui,sans-serif;color:#6366f1;font-weight:600;text-decoration:none;">${label}</a>`;
}

export function customerPortalBadgeHtml(urls: SaasEmbedUrls): string {
  return `<div style="font-family:system-ui,sans-serif;font-size:14px;color:#334155;line-height:1.5;">
  <p style="margin:0 0 8px;">Book services or view your quotes and invoices:</p>
  ${bookNowButtonHtml(urls)}
  <p style="margin:12px 0 0;">Already a customer? ${customerLoginLinkHtml(urls)}</p>
</div>`;
}

export function shopButtonHtml(urls: SaasEmbedUrls, label = 'Shop'): string {
  return `<a href="${urls.store}" target="_blank" rel="noopener noreferrer" style="display:inline-block;padding:12px 24px;background:linear-gradient(135deg,#0f766e,#6366f1);color:#fff;font-family:system-ui,sans-serif;font-weight:600;font-size:16px;text-decoration:none;border-radius:9999px;">${label}</a>`;
}

export function storeEmbedHtml(urls: SaasEmbedUrls): string {
  return `<iframe src="${urls.store}" title="Online store" style="width:100%;min-height:800px;border:0;" loading="lazy"></iframe>`;
}

export function isPlainUrlEmbedSnippet(code: string): boolean {
  return /^https?:\/\//i.test(code.trim());
}
